/**
 * Motor vehicle verification — DMVIC double-insurance + NTSA lookup (customer portal).
 */

import { coverStartIsoFromAny, toLocalISO } from '@/utils/motorCoverDates';

/** Customer self-service double-insurance screening (not agent /legacy double-insurance). */
export const CLIENT_INSURANCE_CHECKER_PATH = '/customer/gis/policies/client-insurance-checker';

function formatDMY(date) {
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

/**
 * DMVIC checker window — always today → today+364 (agent sale rule).
 * User-selected cover start is NOT sent to the checker; it is only used when
 * interpreting overlap against the customer's chosen start date.
 */
export function buildDmvicCheckerDates(referenceDate = new Date()) {
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setDate(end.getDate() + 364);
  return {
    policyStartDate: formatDMY(today),
    policyEndDate: formatDMY(end),
  };
}

/** @deprecated Use buildDmvicCheckerDates for API calls; cover start is for interpretation only. */
export function coverFromToDoubleInsuranceDates(coverFrom) {
  let startDate = new Date();
  if (coverFrom && typeof coverFrom === 'string') {
    if (coverFrom.includes('/')) {
      const [day, month, year] = coverFrom.split('/').map((x) => parseInt(x, 10));
      if (day && month && year) startDate = new Date(year, month - 1, day);
    } else if (coverFrom.includes('-')) {
      const [year, month, day] = coverFrom.split('-').map((x) => parseInt(x, 10));
      if (year && month && day) startDate = new Date(year, month - 1, day);
    }
  }
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 364);
  const endForCheck = new Date(endDate);
  endForCheck.setDate(endForCheck.getDate() - 5);
  return {
    policyStartDate: formatDMY(startDate),
    policyEndDate: formatDMY(endForCheck),
  };
}

function parseDiMessageField(message, key) {
  const m = (message || '').match(new RegExp(`${key}=([^,)]+)`));
  return m ? m[1].trim() : null;
}

function parseDmyToDate(dmy) {
  const [dd, mm, yyyy] = String(dmy || '').split('/');
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Interpret DMVIC response with agent-aligned rules when cover start is user-selected.
 * @param {object} data — API response body
 * @param {string} [coverStartIso] — yyyy-MM-dd chosen cover start
 */
export function interpretDoubleInsuranceResponse(data, coverStartIso) {
  const { result, message } = data || {};

  if (result === 'Validation error' && message !== 'No Records Found') {
    return {
      passed: false, blocked: true, error: 'validation_error', message: message || 'Verification failed',
    };
  }

  if (!result && message && /policy duration/i.test(message)) {
    return {
      passed: false, blocked: true, error: 'validation_error', message,
    };
  }

  if (result === 'Vehicle is not insured' || message === 'No Records Found') {
    return { passed: true, blocked: false };
  }
  if (result !== 'Vehicle is already insured') {
    return { passed: true, blocked: false };
  }

  const coverEndDate = parseDiMessageField(message, 'CoverEndDate');
  const details = {
    coverEndDate,
    certNo: parseDiMessageField(message, 'InsuranceCertificateNo'),
    insurerName: parseDiMessageField(message, 'MemberCompanyName'),
    regNo: parseDiMessageField(message, 'RegistrationNumber'),
    chassisNo: parseDiMessageField(message, 'ChassisNumber'),
  };

  if (!coverEndDate) {
    return {
      passed: false, blocked: true, error: 'double_insurance', details, message,
    };
  }

  const expiry = parseDmyToDate(coverEndDate);
  if (!expiry) {
    return {
      passed: false, blocked: true, error: 'double_insurance', details, message,
    };
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (expiry < now) {
    return { passed: true, blocked: false, expired: true };
  }

  const chosenStart = coverStartIso || coverStartIsoFromAny(null);
  const expiryYear = expiry.getFullYear();
  const currentYear = now.getFullYear();

  if (expiryYear === currentYear) {
    const nextDay = new Date(expiry);
    nextDay.setDate(nextDay.getDate() + 1);
    const expiryPlusOne = toLocalISO(nextDay);
    if (chosenStart >= expiryPlusOne) {
      return {
        passed: true, blocked: false, futureBooking: true, details, message,
      };
    }
    return {
      passed: false,
      blocked: true,
      error: 'double_insurance',
      details,
      earliestStart: expiryPlusOne,
      message,
    };
  }

  return {
    passed: false, blocked: true, error: 'double_insurance', details, message,
  };
}

export async function runDoubleInsuranceCheck(axios, {
  regNo, chassis, coverStartIso,
}) {
  const dates = buildDmvicCheckerDates();
  const res = await axios.post(
    CLIENT_INSURANCE_CHECKER_PATH,
    {
      vehicleRegistrationNumber: regNo,
      policyStartDate: dates.policyStartDate,
      policyEndDate: dates.policyEndDate,
      chassisNumber: chassis || '',
    },
    { timeout: 15000, _skipErrorRedirect: true },
  );
  const iso = coverStartIso || coverStartIsoFromAny(null);
  return interpretDoubleInsuranceResponse(res.data, iso);
}

/** NTSA / DMVIC vehicle search outcomes for UI banners. */
export const NTSA_OUTCOME = {
  FOUND: 'found',
  NOT_FOUND: 'not_found',
  UNAVAILABLE: 'unavailable',
};

export const NTSA_UNAVAILABLE_MESSAGE = 'NTSA vehicle search is temporarily unavailable. Enter the vehicle details manually to continue.';
export const NTSA_TIMEOUT_MESSAGE = 'NTSA vehicle search timed out. Enter the vehicle details manually to continue.';
export const NTSA_NOT_FOUND_MESSAGE = 'No NTSA record was found for this registration. Enter the vehicle details manually below.';

function isAxiosTimeout(err) {
  return err?.code === 'ECONNABORTED'
    || err?.code === 'ETIMEDOUT'
    || /timeout/i.test(String(err?.message || ''));
}

/**
 * NTSA vehicle search that never rejects.
 * Timeouts, network errors, and 5xx/upstream failures unlock manual entry instead of blocking the sale.
 *
 * @returns {Promise<{
 *   outcome: 'found'|'not_found'|'unavailable',
 *   data: object|null,
 *   message: string|null,
 * }>}
 */
export async function fetchNtsaVehicle(axios, regNo) {
  try {
    const res = await axios.post(
      '/customer/gis/policies/vehicleSearch',
      { regNo },
      { timeout: 15000, _skipErrorRedirect: true, _showErrorToast: false },
    );
    const data = res?.data;
    if (!data || data.success === false || !data.Vehicle) {
      return {
        outcome: NTSA_OUTCOME.NOT_FOUND,
        data: data || null,
        message: NTSA_NOT_FOUND_MESSAGE,
      };
    }
    return {
      outcome: NTSA_OUTCOME.FOUND,
      data,
      message: null,
    };
  } catch (err) {
    return {
      outcome: NTSA_OUTCOME.UNAVAILABLE,
      data: null,
      message: isAxiosTimeout(err) ? NTSA_TIMEOUT_MESSAGE : NTSA_UNAVAILABLE_MESSAGE,
    };
  }
}

/** @deprecated Prefer fetchNtsaVehicle().outcome — kept for callers that only need a soft-fail payload. */
export function ntsaLookupFailed(result) {
  return !result || result.outcome === NTSA_OUTCOME.NOT_FOUND
    || result.outcome === NTSA_OUTCOME.UNAVAILABLE;
}

export function mapNtsaToVehicle(ntsaPayload) {
  const v = ntsaPayload?.Vehicle;
  if (!v) return null;
  return {
    engineNumber: (v.EngineNumber || '').toUpperCase(),
    chassisNumber: (v.ChassisNumber || '').toUpperCase(),
    cc: v.cubicCapacity ? String(v.cubicCapacity) : '',
    color: (v.color || v.Color || '').toUpperCase(),
    seatingCapacity: v.passengerCapacity ? String(v.passengerCapacity) : '5',
    make: (v.Make || '').toUpperCase(),
    model: (v.Model || '').toUpperCase(),
    yearOfManufacture: v.RegistrationYear || '',
  };
}

/** True when NTSA make does not match quote vehicle make. */
export function detectQuoteMakeMismatch(quoteCar, ntsaMake) {
  const qMake = (quoteCar?.make || '').toUpperCase().trim();
  const nMake = (ntsaMake || '').toUpperCase().trim();
  if (!qMake || !nMake) return false;
  return !(nMake.includes(qMake) || qMake.includes(nMake));
}
